import api from '@/lib/api';
import type { AssetType, CreateAssetTypeInput, UpdateAssetTypeInput } from '@hygge/shared';
import { CACHE_TIME } from '@hygge/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const assetTypesKeys = {
  all: ['asset-types'] as const,
  lists: () => [...assetTypesKeys.all, 'list'] as const,
  list: () => [...assetTypesKeys.lists()] as const,
};

async function getAssetTypes(includeInactive = false): Promise<AssetType[]> {
  const params = includeInactive ? { includeInactive: 'true' } : {};
  const { data } = await api.get<AssetType[]>('/asset-types', { params });
  return data;
}

async function createAssetType(input: CreateAssetTypeInput): Promise<AssetType> {
  const { data } = await api.post<AssetType>('/asset-types', input);
  return data;
}

async function updateAssetType(id: string, input: UpdateAssetTypeInput): Promise<AssetType> {
  const { data } = await api.put<AssetType>(`/asset-types/${id}`, input);
  return data;
}

async function deleteAssetType(id: string): Promise<void> {
  await api.delete(`/asset-types/${id}`);
}

async function toggleAssetTypeActive(id: string): Promise<AssetType> {
  const { data } = await api.patch<AssetType>(`/asset-types/${id}/toggle-active`);
  return data;
}

// `enabled` lets a caller skip the read when it holds none of the codes the
// catalog reads for (any one of the module's four, since HW-942) — the request
// could only 403.
export function useAssetTypes(includeInactive = false, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [...assetTypesKeys.list(), { includeInactive }],
    queryFn: () => getAssetTypes(includeInactive),
    enabled: options?.enabled ?? true,
    staleTime: CACHE_TIME.DEFAULT,
  });
}

export function useCreateAssetType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createAssetType,
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assetTypesKeys.all });
    },
  });
}

export function useUpdateAssetType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: UpdateAssetTypeInput & { id: string }) =>
      updateAssetType(id, input),
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assetTypesKeys.all });
    },
  });
}

export function useDeleteAssetType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteAssetType,
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assetTypesKeys.all });
      queryClient.refetchQueries({ queryKey: assetTypesKeys.all });
    },
  });
}

export function useToggleAssetTypeActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: toggleAssetTypeActive,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assetTypesKeys.all });
    },
  });
}
